/* Hallmark · macrostructure: Stepped Flow (app) · tone: plain · theme: console-system */
import { Progress, Skeleton } from '@xhubio-saas/core-frontend/ui-components'
import type { SitzungsschluesselZustand } from '@xhubio-saas/invoice-frontend/plugin-invoice-api'
import { FileInput } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  CreatedDocument,
  CreateFailure,
  CreateOutcome,
  CreateParams,
  ExtractFailure,
  ExtractOutcome,
  ExtractResponse,
  OutputKind,
} from '@/api/pdfExtractClient'
import { missingSellerRequirements, type SellerProfile } from '@/api/sellerProfileClient'
import type { ReviewAttachment } from './invoice-from-pdf/attachments'
import { CreateStep } from './invoice-from-pdf/CreateStep'
import {
  computeManualTotals,
  EMPTY_MANUAL_ITEM,
  type ManualItem,
} from './invoice-from-pdf/manual-items'
import { ReviewStep } from './invoice-from-pdf/ReviewStep'
import {
  applyReview,
  type ReviewField,
  type ReviewValues,
  toReviewValues,
} from './invoice-from-pdf/review'
import { type PickedPdf, UploadStep } from './invoice-from-pdf/UploadStep'

/**
 * **E-Rechnung aus PDF** (Plan API 2026-11-01/01, Schritt 4) — das eigene PDF behalten, die Rechnungsdaten auslesen,
 * pruefen und die E-Rechnung anheften. Drei Schritte wie im abgenommenen Entwurf
 * (`tool-saas-admin/workspace/mockups/2026-11-01-e-rechnung-aus-pdf.html`): Hochladen → Daten pruefen → E-Rechnung
 * erstellen. Ausnahme vom Entwurf (E16): keine Markierungen im PDF, die Herkunft steht als Etikett je Feld.
 *
 * 🔴 Die Seite ist eine reine Darstellung — Netz, Sitzungsschluessel, Firmendaten und Kontingent reicht
 * `InvoiceFromPdfPageWithProviders` (`app.pages.tsx`) herein. So prueft der Seitentest jede Zeile der
 * Entscheidungstabelle mit UI-Wirkung (X1, X2, X2b, X5, X8, X10) ohne Netz.
 */

type Step = 1 | 2 | 3

export interface PdfExtractQuota {
  used: number
  limit: number
}

export interface InvoiceFromPdfPageProps {
  /** E7: `upgrade` = der Tarif fuehrt `pdf:invoice:extract` nicht (Free). */
  access: 'allowed' | 'upgrade' | 'loading'
  seller: SellerProfile | null
  sellerLoading?: boolean
  /** Kontingent „Auslesen" — `null` blendet die Anzeige aus (kein begrenztes Kontingent). */
  quota?: PdfExtractQuota | null
  sitzungsschluessel?: SitzungsschluesselZustand
  onExtract: (pdf: string) => Promise<ExtractOutcome>
  onCreate: (params: CreateParams) => Promise<CreateOutcome>
  savedTemplates?: Array<{ id: string; name: string }>
}

const STEPS: readonly Step[] = [1, 2, 3]

export function InvoiceFromPdfPage({
  access,
  seller,
  sellerLoading = false,
  quota = null,
  sitzungsschluessel = 'da',
  onExtract,
  onCreate,
  savedTemplates = [],
}: InvoiceFromPdfPageProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'de'

  const [step, setStep] = useState<Step>(1)
  const [pdf, setPdf] = useState<PickedPdf | null>(null)
  const [extracting, setExtracting] = useState(false)
  const [extractFailure, setExtractFailure] = useState<ExtractFailure | null>(null)
  const [extract, setExtract] = useState<ExtractResponse | null>(null)
  const [extracted, setExtracted] = useState<ReviewValues | null>(null)
  const [values, setValues] = useState<ReviewValues | null>(null)
  const [attachments, setAttachments] = useState<ReviewAttachment[]>([])
  const [manualItems, setManualItems] = useState<ManualItem[] | null>(null)
  const [notRecognized, setNotRecognized] = useState<ExtractResponse | null>(null)

  const [output, setOutput] = useState<OutputKind>('embed')
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<CreatedDocument | null>(null)
  const [createFailure, setCreateFailure] = useState<CreateFailure | null>(null)

  const sellerMissing = useMemo(() => missingSellerRequirements(seller), [seller])

  const changePdf = useCallback((next: PickedPdf | null) => {
    setPdf(next)
    setExtractFailure(null)
    setNotRecognized(null)
  }, [])

  const openReview = useCallback((result: ExtractResponse) => {
    const initial = toReviewValues(result.invoice)
    setExtract(result)
    setExtracted(initial)
    setValues(initial)
    setAttachments([])
    // E19: Ohne erkannte Positionen startet die Eingabe von Hand.
    setManualItems((result.invoice.items ?? []).length === 0 ? [EMPTY_MANUAL_ITEM] : null)
    setCreated(null)
    setCreateFailure(null)
    setNotRecognized(null)
    setStep(2)
  }, [])

  const runExtract = useCallback(async () => {
    if (!pdf) return
    setExtracting(true)
    setExtractFailure(null)
    setNotRecognized(null)
    try {
      const outcome = await onExtract(pdf.content)
      if (!outcome.ok) {
        setExtractFailure(outcome.failure)
        return
      }
      if (outcome.result.recognized === false) {
        setNotRecognized(outcome.result)
        return
      }
      openReview(outcome.result)
    } finally {
      setExtracting(false)
    }
  }, [pdf, onExtract, openReview])

  const changeValue = useCallback((field: ReviewField, value: string) => {
    setValues((prev) => (prev ? { ...prev, [field]: value } : prev))
    // Das Ergebnis gehoert zu der Eingabe, mit der es entstand.
    setCreated(null)
    setCreateFailure(null)
  }, [])

  const runCreate = useCallback(
    async (kind: OutputKind) => {
      if (!pdf || !extract || !values) return
      setCreating(true)
      setCreated(null)
      setCreateFailure(null)
      try {
        const outcome = await onCreate({
          output: kind,
          pdf: pdf.content,
          invoice: applyReview(
            extract.invoice,
            values,
            attachments,
            manualItems ? computeManualTotals(manualItems) : null
          ),
          ...(kind === 'generate' && templateId ? { templateId } : {}),
        })
        if (outcome.ok) setCreated(outcome.result)
        else setCreateFailure(outcome.failure)
      } finally {
        setCreating(false)
      }
    },
    [pdf, extract, values, attachments, manualItems, templateId, onCreate]
  )

  const changeManualItems = useCallback((next: ManualItem[]) => {
    setManualItems(next)
    setCreated(null)
    setCreateFailure(null)
  }, [])

  /** „Pruefen und weiter": die vorgewaehlte Ausgabe mit `validate: true` — die Gueltigkeit kommt aus diesem Aufruf (E6). */
  const next = useCallback(() => {
    setStep(3)
    void runCreate(output)
  }, [runCreate, output])

  /** Andere Anlagen = anderes Dokument: das Ergebnis aus Schritt 3 ist veraltet (E18). */
  const changeAttachments = useCallback((next: ReviewAttachment[]) => {
    setAttachments(next)
    setCreated(null)
    setCreateFailure(null)
  }, [])

  const changeOutput = useCallback((kind: OutputKind) => {
    setOutput(kind)
    setCreated(null)
    setCreateFailure(null)
  }, [])

  // Der laufende Monat, nicht `periodStart`: Das ist der Beginn des Abo-Fensters und lag am 11-04 auf PROD im Juni (E19).
  const month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date())

  return (
    <div className="container mx-auto space-y-6 px-4 py-6" data-testid="invoice-from-pdf-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <FileInput className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
            <h1 className="text-3xl tracking-tight">{t('invoiceFromPdf.page.title')}</h1>
          </div>
          <p className="mt-1 max-w-prose text-sm text-muted-foreground">
            {t('invoiceFromPdf.page.subtitle')}
          </p>
        </div>
        {quota && (
          <div
            className="min-w-48 rounded-lg border bg-card px-3 py-2 text-sm"
            data-testid="invoice-from-pdf-quota"
          >
            <p className="text-muted-foreground">{t('invoiceFromPdf.quota.label', { month })}</p>
            <p className="font-medium tabular-nums">
              {t('invoiceFromPdf.quota.value', { used: quota.used, limit: quota.limit })}
            </p>
            <Progress
              value={quota.limit > 0 ? Math.min(100, (quota.used / quota.limit) * 100) : 100}
              className="mt-1 h-1.5"
              aria-label={t('invoiceFromPdf.quota.label', { month })}
            />
          </div>
        )}
      </div>

      <ol className="grid grid-cols-3 gap-2" aria-label={t('invoiceFromPdf.steps.label')}>
        {STEPS.map((s) => (
          <li
            key={s}
            aria-current={s === step ? 'step' : undefined}
            data-testid={`invoice-from-pdf-step-${s}`}
            className={`border-t-[3px] pt-1.5 text-sm ${
              s === step
                ? 'border-primary font-semibold text-foreground'
                : s < step
                  ? 'border-primary/40 text-muted-foreground'
                  : 'border-border text-muted-foreground'
            }`}
          >
            {s} {t(`invoiceFromPdf.steps.${s}`)}
          </li>
        ))}
      </ol>

      {access === 'loading' || sellerLoading ? (
        <div className="space-y-3" data-testid="invoice-from-pdf-loading">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : step === 1 || !extract || !values || !extracted || !pdf ? (
        <UploadStep
          access={access}
          seller={seller}
          sellerMissing={sellerMissing}
          pdf={pdf}
          onPdfChange={changePdf}
          onExtract={() => void runExtract()}
          running={extracting}
          failure={extractFailure}
          notRecognized={notRecognized !== null}
          onFillManually={() => {
            if (notRecognized) openReview(notRecognized)
          }}
          sessionKey={sitzungsschluessel}
        />
      ) : step === 2 ? (
        <ReviewStep
          pdf={pdf.content}
          extract={extract}
          extracted={extracted}
          values={values}
          onChange={changeValue}
          attachments={attachments}
          onAttachmentsChange={changeAttachments}
          manualItems={manualItems}
          onManualItemsChange={changeManualItems}
          seller={seller}
          onBack={() => setStep(1)}
          onNext={next}
          running={creating}
          locale={locale}
        />
      ) : (
        <CreateStep
          output={output}
          onOutputChange={changeOutput}
          templateId={templateId}
          onTemplateChange={(id) => {
            setTemplateId(id)
            setCreated(null)
            setCreateFailure(null)
          }}
          savedTemplates={savedTemplates}
          result={created}
          failure={createFailure}
          running={creating}
          onCreate={() => void runCreate(output)}
          onBack={() => setStep(2)}
        />
      )}
    </div>
  )
}
